import { Avatar, Card, FormControlLabel, IconButton, Stack, Switch, Tooltip, Typography, useTheme } from '@mui/material'
import BaseWidgetProvider from '../base/BaseWidgetProvider'
import React, { useEffect, useState } from 'react'
import { apiGet, apiPost } from '../../../generic/Api_Functions'
import { useSmartInterval } from '../../../generic/hooks/useSmartInterval'
import { Add, Login, Logout, PauseCircleOutline, PlayCircleOutline } from '@mui/icons-material'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import AlertUI from '../../../generic/AlertUI'
import { widgetStyles } from '../common/Widget'
import LoginDialog from './LoginDialog'
import OtherTaskDialog from './OtherTaskDialog'
import PersonIcon from '@mui/icons-material/Person'
import MosSmartDialog from '../common/MosSmartDialog'
import useMosInfo from '../common/provider/useMosInfo'
import createDialogTitle from '../../../generic/utilities/dialogUtil'

const PresenceWidget = (props) => {
  const { device, worker, setWorker } = useMosInfo()
  const [workers, setWorkers] = useState([])
  const [currentRowWorker, setCurrentRowWorker] = useState(null)
  const [openWindow, setOpenWindow] = useState(false)
  const [openOtherTasksWindow, setOpenOtherTasksWindow] = useState(false)
  const defaultLoginRequest = { workerCode: '', deviceList: [] }
  const [loginRequest, setLoginRequest] = useState(defaultLoginRequest)
  const [mode, setMode] = useState('LOGIN')
  // RN9: local, off by default, never persisted. Reveals the amber rows (workers with an
  // active non-productive task). H1: no auto-activation — not even for the own row.
  const [showNonProductive, setShowNonProductive] = useState(false)
  // HU-05/S17-S19: the "non productive" toggle is shown only when the widget config enables it
  // (props.data.showNonProductiveToggle). Default OFF, incl. pre-existing instances (data null).
  const canToggleNonProductive = props.data?.showNonProductiveToggle === true
  // Widget config "Allow only one logged-in worker": while a worker is logged in on the device, no
  // other login can start from this widget. Default OFF, incl. pre-existing instances (data null).
  const singleWorkerPresence = props.data?.singleWorkerPresence === true
  // HU-06/S20-S23: pending "stop the active non-productive task and log in" offer — { task }.
  const [loginOffer, setLoginOffer] = useState(null)
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert, hideAlert] = AlertUI()
  // Separate, widget-level notice for the AUTO_SWITCH outcome: AlertElement lives inside the login
  // dialog, which unmounts on success, so it cannot show a post-login message.
  const [autoSwitchNotice, showAutoSwitchNotice] = AlertUI()
  const theme = useTheme()
  const styles = widgetStyles(theme)

  useEffect(() => {
    if (openWindow) {
      hideAlert()
    }
  }, [openWindow, hideAlert])

  useSmartInterval(
    async () => {
      if (!device) {
        return null
      }
      return await apiGet('mos/presence/device/' + device.id + '/workers')
    }, 1000, (data) => {
      setWorkers(data)
      // Auto select first worker
      if (data && data.length === 1 && (!worker || worker.id !== data[0].id)) {
        setWorker(data[0])
        return
      }
      // Check if currently selected worker is logged. If not we cannot select this worker
      if (worker && data && !data.find((item) => item.id === worker.id)) {
        setWorker(null)
      }
    }
  )

  // Single-machine presence BLOCK error. The backend rejects the login (non-2xx) with a structured
  // body { error: 'SINGLE_MACHINE_PRESENCE_BLOCKED', workerCode, machineName } (exposed by apiF as
  // error.data). We translate it here with interpolation instead of showing the backend's composed
  // English string; any other error keeps the generic t_(message) path.
  const buildLoginErrorMessage = (error) => {
    const data = error?.data
    if (data && data.error === 'SINGLE_MACHINE_PRESENCE_BLOCKED') {
      const workerCode = data.workerCode || ''
      const machineName = data.machineName || ''
      return t_('Worker {{workerCode}} is already present on machine: {{machineName}} (single-machine presence)', { workerCode, machineName }) +
        '. ' + t_('Please close the session on {{machineName}} before starting a new one.', { machineName })
    }
    return t_(error?.message)
  }

  // Returns true when the presence login actually succeeded, false when the backend rejected it
  // (e.g. single-machine BLOCK) — the alert is shown here in either failure case.
  const submitLogin = async () => {
    const requestWithLogin = { ...loginRequest, deviceList: loginRequest.deviceList.map(d => d.id), login: mode === 'LOGIN' }
    try {
      const response = await apiPost('mos/presence/device', requestWithLogin)
      setOpenWindow(false)
      // AUTO_SWITCH: the backend closed the worker's previous presence on these machines and returns
      // their names so the operator is warned. Empty (or absent) on BLOCK/normal login and on logout.
      const autoSwitchedFrom = response?.autoSwitchedFrom
      if (Array.isArray(autoSwitchedFrom) && autoSwitchedFrom.length > 0) {
        showAutoSwitchNotice({
          severity: 'warning',
          title: t_('Presence automatically switched'),
          message: t_('The previous presence was closed on:') + ' ' + autoSwitchedFrom.join(', ')
        })
      }
      return true
    } catch (e) {
      showAlert({ title: '', message: buildLoginErrorMessage(e), severity: 'error' })
      return false
    }
  }

  const onAccept = async () => {
    // The dialog may have been opened just before another worker logged in (the list polls every second).
    if (mode === 'LOGIN' && singleWorkerPresence && workers?.length > 0) {
      showAlert({ severity: 'error', title: t_('Only one worker can be logged in on this machine') })
      return
    }
    if (!loginRequest.workerCode || loginRequest.workerCode.trim() === '') {
      showAlert({ severity: 'error', title: t_('Please enter a worker code') })
      return
    } else if (loginRequest.deviceList.length === 0) {
      showAlert({ severity: 'error', title: t_('Please select at least one asset') })
      return
    }
    // HU-06/S20-S23: on LOGIN, if the worker has an active non-productive task, offer to stop it and
    // enter (the soft replacement of the login guard retired in N1/S16). Fail-open on error (DELTA-3).
    if (mode === 'LOGIN') {
      try {
        const activeTask = await apiGet('worker/getActiveNonProductiveTasks/' + loginRequest.workerCode)
        if (activeTask && activeTask.endDate === null) {
          setLoginOffer({ task: activeTask })
          return
        }
      } catch (error) {
        // fail-open (DELTA-3): proceed with the login exactly as before.
      }
    }
    await submitLogin()
  }

  const acceptLoginOffer = async () => {
    const { task } = loginOffer
    setLoginOffer(null)
    // Attempt the login FIRST and stop the non-productive task ONLY if it actually succeeded. A
    // single-machine BLOCK must keep the worker on their current machine with their task still
    // running: the offer warned, the user accepted, the block error shows — but it must not cost
    // them the task.
    const loggedIn = await submitLogin()
    if (!loggedIn) {
      return
    }
    try {
      await apiPost('mos/setEnd/nonProductiveTask/' + task.id)
    } catch (error) {
      showAlert({ title: '', message: buildLoginErrorMessage(error), severity: 'error' })
    }
  }

  // DELTA-4: cancelling aborts the alta (no login) and closes the flow.
  const cancelLoginOffer = () => {
    setLoginOffer(null)
    setOpenWindow(false)
  }

  if (workers === null) {
    return null
  }

  const getWorkerInitials = (item) => {
    const nameInitial = item.name?.[0] || ''
    const surnameInitial = item.surname?.[0] || ''
    return `${nameInitial}${surnameInitial}`
  }

  const getStatusColor = () => {
    if (!device?.status || device.status === 'UNKNOWN') {
      return theme.palette.text.secondary
    }
    return theme.palette.info.main
  }

  // RN10/RN16/DELTA-2: green rows always visible; amber rows only when the toggle is both
  // available (widget config, HU-05) AND on. Config off/absent → toggle hidden and amber hidden.
  const showAmber = canToggleNonProductive && showNonProductive
  const visibleWorkers = workers.filter((item) => showAmber || !item.activeNonProductiveTask)
  // Counted on every logged-in worker, not only the visible ones: a worker on a non-productive task
  // is still logged in even when their row is hidden.
  const loginBlocked = singleWorkerPresence && workers.length > 0

  return (
    <>
      <Stack sx={{ height: '100%', gap: 1 }}>
        {autoSwitchNotice}
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <Stack spacing={0.25}>
            <Typography sx={styles.subtitle2}>{t_('Presence')}</Typography>
            <Typography variant='body2' color='text.secondary'>
              {t_('Active shift')}
            </Typography>
          </Stack>
          <Stack direction='row' spacing={1} alignItems='center'>
            {canToggleNonProductive && (
              <FormControlLabel
                sx={{ marginRight: 0 }}
                control={
                  <Switch
                    checked={showNonProductive}
                    onChange={(event) => setShowNonProductive(event.target.checked)}
                  />
                }
                label={
                  <Typography variant='caption' color='text.secondary'>
                    {t_('Show non-productive')}
                  </Typography>
                }
              />
            )}
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='center'
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 999,
                border: '1px solid ' + theme.palette.divider,
                minWidth: 32
              }}
            >
              <Typography variant='caption' color='text.secondary'>
                {workers.length}
              </Typography>
            </Stack>
            {!loginBlocked && (
              <IconButton
                aria-label={t_('Add worker')}
                onClick={() => {
                  setMode('LOGIN')
                  setLoginRequest({ ...defaultLoginRequest, deviceList: device ? [device] : [] })
                  setOpenWindow(true)
                }}
                sx={{
                  borderRadius: '12px',
                  border: '1px solid ' + theme.palette.divider,
                  width: 44,
                  height: 44,
                  backgroundColor: theme.palette.background.button,
                  '&:hover': {
                    backgroundColor: theme.palette.background.buttonHover
                  }
                }}
              >
                <Add fontSize='medium' />
              </IconButton>
            )}
          </Stack>
        </Stack>

        <Stack
          sx={{
            flexGrow: 1,
            overflowY: 'auto'
          }}
          spacing={0.75}
        >
          {visibleWorkers.map(item => {
            const isSelected = item.id === worker?.id
            const isNonProductive = Boolean(item.activeNonProductiveTask)
            return (
              <Stack
                key={item.id}
                data-non-productive={isNonProductive ? 'true' : undefined}
                direction='row'
                alignItems='center'
                justifyContent='space-between'
                sx={{
                  borderRadius: 1,
                  // Amber (typical absence color) marks an active non-productive task on the WHOLE
                  // border; same row content, only the colour changes (RN10).
                  border: '1px solid ' + (isNonProductive
                    ? '#eab308'
                    : (isSelected ? theme.palette.primary.main : theme.palette.divider)),
                  borderLeftWidth: 3,
                  borderLeftColor: isNonProductive
                    ? '#eab308'
                    : (isSelected ? theme.palette.primary.main : theme.palette.divider),
                  backgroundColor: isSelected ? theme.palette.action.selected : 'transparent',
                  paddingX: 1,
                  paddingY: 0.75,
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease, border-color 0.15s ease'
                }}
                onClick={() => setWorker(item)}
              >
                <Stack direction='row' spacing={1} alignItems='center' sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: '0.75rem',
                      bgcolor: isNonProductive
                        ? '#eab308'
                        : (isSelected ? theme.palette.primary.main : theme.palette.background.chip),
                      color: (isNonProductive || isSelected) ? theme.palette.background.default : theme.palette.text.primary
                    }}
                  >
                    {getWorkerInitials(item)}
                  </Avatar>
                  <Stack spacing={0.25} sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography
                      variant='body2'
                      noWrap
                      sx={{ fontWeight: isSelected ? 600 : 500 }}
                    >
                      {item.code}
                    </Typography>
                    <Typography
                      variant='caption'
                      noWrap
                      sx={{
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                        color: getStatusColor()
                      }}
                    >
                      {item.name + ' ' + item.surname}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack direction='row' spacing={0.5} alignItems='center'>
                  <Tooltip
                    title={isNonProductive
                      ? t_('This non productive task is active:') + ' ' +
                        (item.activeNonProductiveTaskName || item.activeNonProductiveTaskCode || '')
                      : t_('Start non-productive task')}
                  >
                    <IconButton
                      size='small'
                      aria-label={isNonProductive ? t_('Stop non-productive task') : t_('Start non-productive task')}
                      onClick={() => {
                        setCurrentRowWorker(item)
                        setOpenOtherTasksWindow(true)
                      }}
                    >
                      {isNonProductive
                        ? <PlayCircleOutline />
                        : <PauseCircleOutline />}
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    size='small'
                    color='error'
                    onClick={() => {
                      setMode('LOGOUT')
                      setLoginRequest({ workerCode: item.code, deviceList: [device] })
                      setOpenWindow(true)
                    }}
                  >
                    <Logout fontSize='small' />
                  </IconButton>
                </Stack>
              </Stack>
            )
          })}
        </Stack>
      </Stack>
      <MosSmartDialog
        title={{
          icon: mode === 'LOGIN' ? <Login /> : <Logout />,
          render: (
            <Typography variant='h4'>
              {mode === 'LOGIN' ? t_('Login worker') : t_('Logout worker')}
            </Typography>
          )
        }}
        setOpen={openWindow}
        acceptCallback={() => onAccept()}
        cancelCallback={() => setOpenWindow(false)}
      >
        <LoginDialog
          request={loginRequest}
          setRequest={setLoginRequest}
          AlertElement={AlertElement}
        />
      </MosSmartDialog>
      <OtherTaskDialog
        open={openOtherTasksWindow}
        setOpen={setOpenOtherTasksWindow}
        worker={currentRowWorker}
      />
      {loginOffer && (
        <MosSmartDialog
          title={createDialogTitle({ color: 'warning', title: t_('Active non-productive task'), icon: PauseCircleOutline })}
          setOpen
          acceptLabel='Stop task and log in'
          acceptCallback={() => acceptLoginOffer()}
          cancelCallback={() => cancelLoginOffer()}
        >
          <Typography sx={{ padding: '1rem' }}>
            {t_('This non productive task is active:')}{' '}
            <Typography component='span' sx={{ fontWeight: 700, color: '#eab308' }}>
              {loginOffer.task.otherTask?.name || loginOffer.task.otherTask?.code || ''}
            </Typography>
          </Typography>
        </MosSmartDialog>
      )}
    </>
  )
}

const PresenceWidgetPreview = (props) => {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const styles = widgetStyles(theme)
  return (
    <Card
      sx={{
        ...styles.card,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '0.75rem 1rem'
      }}
    >
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Typography>{t_('Presence')}</Typography>
        <Typography sx={styles.subtitle2}>{t_('Add worker')}</Typography>
      </Stack>
      <Stack sx={{ marginTop: '3%' }}>
        <Card
          sx={{
            ...styles.card,
            height: 'auto',
            padding: '0.5rem 0.75rem',
            borderStyle: 'dashed',
            borderColor: theme.palette.divider,
            backgroundColor: 'transparent'
          }}
        >
          <Typography sx={styles.subtitle2}>{t_('List of workers')}</Typography>
        </Card>
      </Stack>
    </Card>
  )
}

class PresenceWidgetProvider extends BaseWidgetProvider {
  getCode = () => 'PresenceWidgetProvider'

  getName = () => 'Presence'

  getIcon = () => PersonIcon

  getStatus (globalData) {
    return globalData?.workerRequested ? 'warning' : null
  }

  // HU-05/S17-S19: expose the "non productive" toggle as a per-widget boolean config (⚙️ Edit).
  // Default OFF → the toggle is hidden unless explicitly enabled; pre-existing instances (data null)
  // resolve to false with no migration.
  getConfigurationInfo = () => {
    return [
      {
        key: 'showNonProductiveToggle',
        type: 'boolean',
        label: 'Show non-productive workers toggle',
        defaultValue: false
      },
      {
        key: 'singleWorkerPresence',
        type: 'boolean',
        label: 'Allow only one logged-in worker',
        defaultValue: false
      }
    ]
  }

  createWidget = (props) => <PresenceWidget {...props} />

  createPreviewWidget = (props) => <PresenceWidgetPreview {...props} />

  size = { defaultW: 2, defaultH: 3, minW: 2, minH: 3 }

  getWidgetInfo = (columns, cellHeight) => {
    return { ...super.getWidgetInfo(columns, cellHeight, this.size) }
  }
}

export default PresenceWidgetProvider
